import { Button, TextField, Grid, Typography, ToggleButton, ToggleButtonGroup, Box, Divider, TextFieldProps, CircularProgress, InputAdornment } from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { AccountData, BankInquiryResult } from "../../../../types"
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useForm, Controller, SubmitHandler, FormProvider, UseControllerProps, ValidationRule } from 'react-hook-form'
import { UploadButton } from '../../UploadButton'
import { BaseTextFieldProps } from '@mui/material/TextField/TextField'
import { useSubmitAccountVerification } from '../../../../logic/useApiMutation'
import { useLocation, useParams } from 'react-router-dom'
import { useOptimisticUpdateNotification, useOptimisticUpdateNotificationAndOrderNotification } from "../../../../logic/optimisticUpdates"
import { useNotificationContentOverride } from "../../../context/notification-content-override/NotificationContentOverrideContext"
import { track } from "../../../../logic"
import { ErrorDialog } from "../../dialogs/ErrorDialog"
import { useGetBankInformation, useGetCustomFields } from "../../../../logic/useApiQuery"
import { useQueryClient } from "@tanstack/react-query"
import { apiRoutes } from "../../../../logic/apiRoutes"

interface AccountVerificationFormInputs {
  firstName: string | undefined
  middleName: string | undefined
  lastName: string | undefined
  businessName: string | undefined
  accountNumber: string
  accountNumberConfirm: string
  routingNumber: string
  address: string
  city: string
  state: string
  zip: string
  ssn: string
  dateOfBirth: string
  workPhone: string
  homePhone: string
  bankName: string
  specialInstructions: string
  intermediaryBankName: string
  intermediaryBankNumber: string
}

export function AccountVerificationForm() {

  const formMethods = useForm<AccountVerificationFormInputs>({
    defaultValues: {
      firstName: undefined,
      middleName: undefined,
      lastName: undefined,
      businessName: undefined,
      accountNumber: '',
      accountNumberConfirm: '',
      routingNumber: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      ssn: '',
      dateOfBirth: '',
      workPhone: '',
      homePhone: '',
      bankName: '',
      specialInstructions: '',
      intermediaryBankName: '',
      intermediaryBankNumber: '',
    },
    mode: 'onTouched'
  })

  const queryClient = useQueryClient()
  const { search } = useLocation()
  const { closingId, id } = useParams()
  const { data: customFields } = useGetCustomFields(closingId ?? '', search)
  const USBankRoutingVerificationEnabled = Boolean(customFields?.USBankRoutingVerification)
  const { optimisticUpdateNotification } = useOptimisticUpdateNotification()
  const { optimisticUpdateNotificationAndOrderNotification } = useOptimisticUpdateNotificationAndOrderNotification()
  const { setNotificationContentOverride } = useNotificationContentOverride()

  const routingNumber = formMethods.watch('routingNumber')

  const { data: bankInformation, refetch: refetchBankInformation, isFetching: bankInformationFetching, error: bankInformationError } = useGetBankInformation(closingId ?? '', id ?? '', routingNumber, {
    enabled: false,
    refetchOnMount: false,
    staleTime: Infinity
  })

  const SUBMIT_ACCOUNT_VERIFICATION_LOADING_TEXT = 'Determining next steps...'
  const submitAccountVerificationRequest = useSubmitAccountVerification(closingId ?? '', id ?? '', search, {
    onMutate: () => {
      setNotificationContentOverride(SUBMIT_ACCOUNT_VERIFICATION_LOADING_TEXT)
    },
    onSuccess: (data) => {
      if (!data.offerPlaid) {
        optimisticUpdateNotificationAndOrderNotification({ orderKey: closingId ?? '', notificationId: id ?? '', search, patch: { IsComplete: true } })
      } else {
        optimisticUpdateNotification({ orderKey: closingId ?? '', notificationId: id ?? '', search, patch: { IsPlaidOffered: true } })
      }
    },
  })

  const onSubmit: SubmitHandler<AccountVerificationFormInputs> = async (data: AccountVerificationFormInputs): Promise<void> => {

    if (USBankRoutingVerificationEnabled && (!bankInformation || bankInformation.status !== 'success' || bankInformation.canWire === false)) {
      if (bankInformation) {
        processBankData(bankInformation)
      }
      return
    }

    // Create a copy of the data to avoid modifying the original
    const formDataCopy = { ...data }

    // Remove non-numeric characters in phone numbers
    if (formDataCopy.homePhone) {
      formDataCopy.homePhone = formDataCopy.homePhone.replace(/\D/g, '')
    }
    if (formDataCopy.workPhone) {
      formDataCopy.workPhone = formDataCopy.workPhone.replace(/\D/g, '')
    }

    // The 3rd party validation api doesn't like '' or null values.
    if (individualOrBusiness === 'individual') {
      formDataCopy.businessName = undefined
    } else {
      formDataCopy.firstName = undefined
      formDataCopy.lastName = undefined
    }

    // Transform form data to AccountData type
    const accountData: AccountData = formDataCopy as unknown as AccountData

    void submitAccountVerificationRequest.mutateAsync({ account: accountData })
  }

  const [individualOrBusiness, setIndividualOrBusiness] = useState<'individual' | 'business'>('individual')

  const processBankData = useCallback((data: BankInquiryResult) => {
    formMethods.clearErrors('routingNumber')

    if (data.status === 'success') {
      formMethods.setValue('bankName', data.bankName)
      if (!data.canWire) {
        formMethods.setError('routingNumber', {
          type: 'validate',
          message: 'This routing number does not support wire transfers. Please contact your bank for the correct routing number.'
        })
      }
    } else if (data.status === 'not-found') {
      formMethods.setValue('bankName', '')
      formMethods.setError('routingNumber', {
        type: 'validate',
        message: 'No bank found for this routing number. Please verify and try again.'
      })
    } else if (data.status === 'invalid') {
      formMethods.setValue('bankName', '')
      formMethods.setError('routingNumber', {
        type: 'validate',
        message: 'Invalid routing number. Please enter a valid 9-digit routing number.'
      })
    }
  }, [formMethods])

  //This is the equivalent of onSuccess of the bank information fetch.
  useEffect(() => {
    if (bankInformationFetching || !bankInformation || !routingNumber) {
      return
    }
    processBankData(bankInformation)
  }, [bankInformation, bankInformationFetching, routingNumber, processBankData])

  useEffect(() => {
    if (bankInformationError) {
      formMethods.setError('routingNumber', {
        type: 'validate',
        message: 'There was an error validating the routing number.'
      })
    }
  }, [bankInformationError, formMethods])


  const textFieldProps: TextFieldProps = useMemo(() => ({
    variant: "outlined",
    fullWidth: true,
    sx: {
      backgroundColor: 'white',
    },
    margin: 'normal',
  }), [])

  function minAndMaxLength(
    minLength: ValidationRule<number> | undefined,
    maxLength: ValidationRule<number> | undefined,
    label: React.ReactNode
  ): {
    minLength?: { value: number; message: string }
    maxLength?: { value: number; message: string }
  } {
    const labelStr = String(label)
    const minValue = typeof minLength === 'number' ? minLength : minLength?.value
    const maxValue = typeof maxLength === 'number' ? maxLength : maxLength?.value

    if (minValue && maxValue && minValue === maxValue) {
      return {
        minLength: {
          value: minValue,
          message: `${labelStr} must be exactly ${minValue} characters`
        },
        maxLength: {
          value: maxValue,
          message: `${labelStr} must be exactly ${maxValue} characters`
        }
      }
    }

    return {
      minLength: minValue ? {
        value: minValue,
        message: `${labelStr} must be at least ${minValue} characters`
      } : undefined,
      maxLength: maxValue ? {
        value: maxValue,
        message: `${labelStr} must be at most ${maxValue} characters`
      } : undefined,
    }
  }

  const FormTextField = useMemo(() => {
    return ({
      name,
      label,
      rules = {},
      ...rest
    }: {
      name: UseControllerProps<AccountVerificationFormInputs>['name'],
      label: BaseTextFieldProps['label'],
      rules?: UseControllerProps<AccountVerificationFormInputs>['rules'],
    } & Omit<TextFieldProps, 'name' | 'label' | 'rules'>): JSX.Element => {
      return (
        <Controller
          name={name}
          control={formMethods.control}
          rules={{
            ...rules,
            required: rules.required ? `${label} is required` : undefined,
            ...minAndMaxLength(rules.minLength, rules.maxLength, label),
          }}
          render={({ field, fieldState }) => (
            <TextField
              id={name}
              {...field}
              {...textFieldProps}
              {...rest}
              label={label}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || rest.helperText}
              required={rules.required ? true : false}
            />
          )}
        />
      )
    }
  }, [formMethods.control, textFieldProps])

  //TODO: Either make this a common method or use some sort of global toast so that the toast could get populated inside of the onError in the useMutation
  if (submitAccountVerificationRequest.isError) {
    track('error', 'submitting account verification request', {}, {
      message: JSON.stringify({
        submitAccountVerificationRequest: submitAccountVerificationRequest.error,
      }),
      component: 'AccountVerificationTask',
    })
    return <ErrorDialog error='There was an error processing your request.' tryAgainText='Reload and try again' />
  }

  return (
    <FormProvider {...formMethods}>
      <form noValidate onSubmit={formMethods.handleSubmit(onSubmit)}>
        <Box sx={{ p: 2, mb: 1 }}>
          <Typography variant="h6">Account Ownership Details</Typography>

          <Grid container justifyContent="center" mt={2}>
            <ToggleButtonGroup
              value={individualOrBusiness}
              exclusive
              onChange={(_, value) => {
                if (value && value !== individualOrBusiness) {
                  setIndividualOrBusiness(value)
                }
              }}
              fullWidth
            >
              <ToggleButton value="individual" color="primary">
                Individual
              </ToggleButton>
              <ToggleButton value="business" color="primary">
                Business/LLC/Trust
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* Conditionally render the input fields based on selected option */}
          {individualOrBusiness === 'business' ? (
            <FormTextField
              name="businessName"
              label="Business Name"
              rules={{ required: "Business Name is required", maxLength: 40 }}
              inputProps={{ maxLength: 40 }}
            />
          ) : (
            <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
              <Grid item xs={12} lg={4}>
                <FormTextField
                  name="firstName"
                  label="First Name"
                  rules={{ required: true, maxLength: 40 }}
                  inputProps={{ maxLength: 40 }}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <FormTextField
                  name="middleName"
                  label="Middle Name"
                  rules={{ maxLength: 40 }}
                  inputProps={{ maxLength: 40 }}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <FormTextField
                  name="lastName"
                  label="Last Name"
                  rules={{ required: true, maxLength: 40 }}
                  inputProps={{ maxLength: 40 }}
                />
              </Grid>
            </Grid>
          )}
          <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
            <Grid item xs={12} lg={6} xl={3}>
              <FormTextField
                name="address"
                label="Street Address"
                rules={{ required: true, maxLength: 40 }}
                inputProps={{ maxLength: 40 }}
              />
            </Grid>
            <Grid item xs={12} lg={6} xl={3}>
              <FormTextField
                name="city"
                label="City"
                rules={{ required: true, maxLength: 25 }}
                inputProps={{ maxLength: 25 }}
              />
            </Grid>
            <Grid item xs={12} lg={6} xl={3}>
              <FormTextField
                name="state"
                label="State"
                rules={{
                  required: true, minLength: 2, maxLength: 2,
                  pattern: {
                    value: /^[A-Z]{2}$/,
                    message: "Please enter a valid state abbreviation"
                  }
                }}
                inputProps={{ maxLength: 2, minLength: 2 }}
              />
            </Grid>
            <Grid item xs={12} lg={6} xl={3}>
              <FormTextField
                name="zip"
                label="Zipcode"
                rules={{
                  required: true, minLength: 5, maxLength: 5,
                  pattern: {
                    value: /^\d+$/,
                    message: "Please enter numbers only"
                  }
                }}
                inputProps={{ maxLength: 5, minLength: 5 }}
              />
            </Grid>
          </Grid>

          {individualOrBusiness === 'individual' && <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
            <Grid item xs={12} lg={6}>
              <Controller
                name="dateOfBirth"
                control={formMethods.control}
                rules={{
                  validate: (value) => {
                    return value.length === 0 || value.length === 8 ? true : "Please enter a valid date"
                  }
                }}
                render={({ field, fieldState }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Date of Birth"
                      value={field.value ? new AdapterDayjs().date(field.value) : null}
                      onChange={(date) => field.onChange(date ? date.format('YYYYMMDD') : '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          {...textFieldProps}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormTextField
                name="ssn"
                label="Last 4 of SSN"
                rules={{
                  minLength: 4,
                  maxLength: 4,
                  pattern: {
                    value: /^\d+$/,
                    message: "Please enter numbers only"
                  }
                }}
                inputProps={{ maxLength: 4, minLength: 4 }}
              />
            </Grid>
          </Grid>}

          <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
            <Grid item xs={12} lg={individualOrBusiness === 'individual' ? 6 : 12}>
              <FormTextField
                name="workPhone"
                label={individualOrBusiness === 'individual' ? "Work Phone" : "Phone"}
                rules={{
                  pattern: {
                    value: /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
                    message: "Please enter a valid phone number XXX-YYY-ZZZZ",
                  },
                }}
                inputProps={{ maxLength: 12 }}
              />
            </Grid>
            {individualOrBusiness === 'individual' && <Grid item xs={12} lg={6} >
              <FormTextField
                name="homePhone"
                label="Home Phone"
                rules={{
                  pattern: {
                    value: /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
                    message: "Please enter a valid phone number XXX-YYY-ZZZZ",
                  },
                }}
                inputProps={{ maxLength: 12 }}
              />
            </Grid>}
          </Grid>
        </Box>

        <Divider />

        <Box sx={{ p: 2, mb: 1 }}>
          <Typography variant="h6">Banking Information</Typography>
          { USBankRoutingVerificationEnabled && <>
            <FormTextField
              name="routingNumber"
              label="ABA Routing Number"
              rules={{
                required: true,
                minLength: 9,
                maxLength: 9,
                pattern: {
                  value: /^\d+$/,
                  message: "Please enter numbers only"
                },
              }}
              helperText={bankInformationFetching ? "Verifying routing number..." : null}
              inputProps={{ maxLength: 9, minLength: 9 }}
              onChange={async (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                formMethods.setValue('routingNumber', e.target.value)
                // Clear only the custom bank validation errors when user is typing
                // This allows react-hook-form's built-in validation to show
                const currentError = formMethods.formState.errors.routingNumber
                if (currentError && currentError.type === 'validate' && e.target.value.length < 9) {
                  formMethods.clearErrors('routingNumber')
                  // Trigger validation to show the built-in length/pattern errors
                  await formMethods.trigger('routingNumber')
                } else if (e.target.value.length === 9) { //Don't show any errors if the length is 9 and it wasn't a validation error.
                  formMethods.clearErrors('routingNumber')
                }
              }}
              onBlur={async (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                if (e.target.value.length !== 9 || !/^\d+$/.test(e.target.value)) {
                  await formMethods.trigger('routingNumber')
                  return
                }
                formMethods.clearErrors('routingNumber')
                const queryKey = [apiRoutes.bankInformation(closingId ?? '', id ?? '', e.target.value)]
                const cachedData = queryClient.getQueryData(queryKey) as BankInquiryResult | undefined

                if (cachedData) {
                  processBankData(cachedData)
                  return
                }

                void refetchBankInformation()
              }}
              InputProps={{
                readOnly: bankInformationFetching,
                startAdornment: bankInformationFetching ? (
                  <InputAdornment position="start">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null,
              }}
            />
            <FormTextField
              name="bankName"
              label="Bank Name"
              helperText='Bank name will be populated automatically based on the routing number.'
              rules={{
                maxLength: 40,
              }}
              InputProps={{
                startAdornment: bankInformationFetching ? (
                  <InputAdornment position="start">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null,
              }}
              inputProps={{ maxLength: 40, readOnly: true }}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: '#f5f5f5', // Light gray to indicate non-editable
                  cursor: 'default',
                },
                '& .MuiInputBase-input': {
                  cursor: 'default',
                  color: 'text.secondary', // Slightly muted text color
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.15)', // Lighter border
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                }
              }}
            />
          </>}
          {!USBankRoutingVerificationEnabled && <>
            <FormTextField
              name="routingNumber"
              label="ABA Routing Number"
              rules={{
                required: true,
                minLength: 9,
                maxLength: 9,
                pattern: {
                  value: /^\d+$/,
                  message: "Please enter numbers only"
                },
              }}
              inputProps={{ maxLength: 9, minLength: 9 }}
            />
            <FormTextField
              name="bankName"
              label="Bank Name"
              rules={{
                maxLength: 40,
              }}
              inputProps={{ maxLength: 40 }}
            />
          </>}
          <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
            <Grid item xs={12} lg={6}>
              <FormTextField
                name="accountNumber"
                label="Account Number"
                rules={{
                  required: true,
                  maxLength: 17,
                  deps: "accountNumberConfirm",
                  pattern: {
                    value: /^[A-Za-z0-9]+$/,
                    message: "Please enter letters and numbers only"
                  }
                }}
                inputProps={{ maxLength: 17 }}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormTextField
                name="accountNumberConfirm"
                label="Confirm Account Number"
                rules={{
                  required: true,
                  maxLength: 17,
                  validate: (value: string | undefined): string | true =>
                    value === formMethods.getValues("accountNumber") || "Account numbers do not match",
                  deps: "accountNumber",
                  pattern: {
                    value: /^[A-Za-z0-9]+$/,
                    message: "Please enter letters and numbers only"
                  }
                }}
                inputProps={{ maxLength: 17 }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box sx={{ p: 2, mb: 1 }}>
          <Typography variant="h6">Intermediary Bank Information</Typography>
          <FormTextField
            name="intermediaryBankName"
            label="Intermediary Bank Name"
            rules={{ maxLength: 40 }}
            inputProps={{ maxLength: 40 }}
          />
          <FormTextField
            name="intermediaryBankNumber"
            label="Intermediary Bank Routing Number"
            rules={{
              minLength: 9,
              maxLength: 9,
              pattern: {
                value: /^\d+$/,
                message: "Please enter numbers only"
              }
            }}
            inputProps={{ maxLength: 9 }}
          />
        </Box>

        <Box sx={{ p: 2, mb: 1 }}>
          <Typography variant="h6">Additional Information</Typography>
          <FormTextField //Warning: the browser will tell you it's missing an id.  Look here as to why:  https://github.com/mui/material-ui/issues/40859
            name="specialInstructions"
            label="Special Instructions"
            rules={{ maxLength: 1024 }}
            multiline
            rows={4}
          />
          <Grid container alignItems="center" gap={2} mt={2} bgcolor={"white"} borderRadius={"4px"} padding={"10px"} border={"1px solid rgba(0,0,0,0.175)"} wrap="nowrap">
            <Grid item>
              <UploadButton />
            </Grid>
            <Grid item>
              <Typography paddingLeft={1}>
                If your bank has provided a document with additional instructions please upload it here.
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Grid container justifyContent="flex-end" sx={{ pr: 2 }}>
          <Button
            type="submit"
            sx={{
              mt: 2,
              pr: 2
            }}
            variant="contained"
          >
            Submit
          </Button>
        </Grid>
      </form>
    </FormProvider>
  )
}
